import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Typography,
  Box,
  Chip,
  Popover,
  Paper,
  Button,
} from "@mui/material";
import RepeatIcon from "@mui/icons-material/Repeat";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const WEEKDAYS = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const CalendarNumberPicker = ({
  value,
  onChange,
  disabled,
  selectedDayNumbers,
  startDate,
  endDate,
  index,
}: {
  value: string;
  onChange: (dayNumber: number | null) => void;
  disabled: boolean;
  selectedDayNumbers: number[];
  startDate: string;
  endDate: string;
  index: number;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const selectedDayNumber = value ? new Date(value).getDate() : null;

  const getMonthsWithoutDay = (day: number) => {
    if (!startDate || !endDate || day <= 28) return [];

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const monthsWithoutDay: string[] = [];

    let currentMonth = start.startOf("month");
    const endMonth = end.startOf("month");

    while (
      currentMonth.isBefore(endMonth, "month") ||
      currentMonth.isSame(endMonth, "month")
    ) {
      const daysInMonth = currentMonth.daysInMonth();
      if (day > daysInMonth) {
        monthsWithoutDay.push(currentMonth.format("MMMM YYYY"));
      }
      currentMonth = currentMonth.add(1, "month");
    }

    return monthsWithoutDay;
  };

  const monthsWithoutSelectedDay = selectedDayNumber
    ? getMonthsWithoutDay(selectedDayNumber)
    : [];

  const handleOpen = (event: React.MouseEvent<HTMLInputElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDaySelect = (dayNumber: number) => {
    if (selectedDayNumbers.includes(dayNumber)) {
      return;
    }
    onChange(dayNumber);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? `calendar-popover-${index}` : undefined;

  const calendarDays = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <>
      <TextField
        fullWidth
        size="small"
        value={selectedDayNumber ? selectedDayNumber.toString() : ""}
        placeholder="Select day"
        disabled={disabled}
        onClick={handleOpen}
        inputProps={{
          readOnly: true,
        }}
        sx={{
          cursor: disabled ? "not-allowed" : "pointer",
          "& .MuiOutlinedInput-root": {
            cursor: disabled ? "not-allowed" : "pointer",
            "& input": {
              cursor: disabled ? "not-allowed" : "pointer",
              caretColor: "transparent",
            },
          },
        }}
      />
      {monthsWithoutSelectedDay.length > 0 && (
        <Box
          sx={{ mt: 0.5, p: 1, bgcolor: "warning.lighter", borderRadius: 1 }}
        >
          <Typography
            variant="caption"
            color="warning.dark"
            sx={{ display: "block", fontWeight: 500 }}
          >
            ⚠️ {selectedDayNumber} does not exist in:{" "}
            {monthsWithoutSelectedDay.join(", ")}. Task will default to the last
            day of those months.
          </Typography>
        </Box>
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Paper sx={{ p: 2, minWidth: 280, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: "center" }}>
            Select Day
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 0.5,
            }}
          >
            {calendarDays.map((day) => {
              const isSelected = selectedDayNumber === day;
              const isDisabled = selectedDayNumbers.includes(day);
              const monthsWithoutDay = getMonthsWithoutDay(day);
              const hasWarning = monthsWithoutDay.length > 0;

              let isValidInRange = false;
              if (startDate && endDate) {
                const start = dayjs(startDate);
                const end = dayjs(endDate);
                let currentMonth = start.startOf("month");
                const endMonth = end.startOf("month");

                while (
                  currentMonth.isBefore(endMonth, "month") ||
                  currentMonth.isSame(endMonth, "month")
                ) {
                  const daysInMonth = currentMonth.daysInMonth();
                  if (day <= daysInMonth) {
                    const candidateDate = currentMonth.date(day);
                    if (
                      candidateDate.isValid() &&
                      candidateDate.date() === day &&
                      (candidateDate.isAfter(start, "day") ||
                        candidateDate.isSame(start, "day")) &&
                      (candidateDate.isBefore(end, "day") ||
                        candidateDate.isSame(end, "day"))
                    ) {
                      isValidInRange = true;
                      break;
                    }
                  }
                  currentMonth = currentMonth.add(1, "month");
                }
              }

              return (
                <Button
                  key={day}
                  onClick={() =>
                    !isDisabled && isValidInRange && handleDaySelect(day)
                  }
                  disabled={isDisabled || !isValidInRange}
                  title={
                    hasWarning && !isDisabled && isValidInRange
                      ? `Note: ${monthsWithoutDay.join(", ")} ${monthsWithoutDay.length === 1 ? "does" : "do"} not have ${day} days`
                      : ""
                  }
                  sx={{
                    minWidth: 32,
                    height: 32,
                    p: 0,
                    fontSize: "0.875rem",
                    bgcolor: isSelected ? "primary.main" : "transparent",
                    color: isSelected
                      ? "primary.contrastText"
                      : hasWarning && !isDisabled && isValidInRange
                        ? "warning.main"
                        : "text.primary",
                    border:
                      hasWarning && !isDisabled && isValidInRange
                        ? "1px solid"
                        : "none",
                    borderColor:
                      hasWarning && !isDisabled && isValidInRange
                        ? "warning.main"
                        : "transparent",
                    "&:hover": {
                      bgcolor: isSelected
                        ? "primary.dark"
                        : isDisabled || !isValidInRange
                          ? "transparent"
                          : "action.hover",
                    },
                    "&.Mui-disabled": {
                      opacity: isDisabled ? 0.5 : 0.3,
                      cursor: "not-allowed",
                    },
                  }}
                >
                  {day}
                </Button>
              );
            })}
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default function Recurrence({
  value,
  onChange,
  onValidityChange,
  taskStartDate,
  disableStartDate = false,
  onDuplicateDayNumbersChange,
  onRecurrenceDatePickerOpen,
  closeRecurrencePickers,
}: any) {
  const [frequency, setFrequency] = useState<
    "WEEKLY" | "MONTHLY" | "CUSTOM" | ""
  >(value?.frequency || "");
  const [interval, setInterval] = useState<number>(value?.interval || 1);
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>(
    value?.selectedWeekdays || [],
  );
  const [monthlyDates, setMonthlyDates] = useState<string[]>(
    value?.monthlyDates || [],
  );
  const normalizeCustomDates = (val: any): string[] => {
    if (!val) return [];
    if (Array.isArray(val))
      return val.filter((d): d is string => typeof d === "string" && d !== "");
    if (val?.dates && Array.isArray(val.dates)) {
      return val.dates.map((ts: number) => dayjs(ts).format("YYYY-MM-DD"));
    }
    return [];
  };
  const [customDates, setCustomDates] = useState<string[]>(() =>
    normalizeCustomDates(value?.customDates),
  );
  const [lastSelectedCustomDate, setLastSelectedCustomDate] =
    useState<string>("");
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [customDateOpen, setCustomDateOpen] = useState(false);

  React.useEffect(() => {
    if (closeRecurrencePickers) {
      setStartOpen(false);
      setEndOpen(false);
      setCustomDateOpen(false);
    }
  }, [closeRecurrencePickers]);

  const startDate =
    disableStartDate && taskStartDate ? taskStartDate : value?.startDate || "";
  const endDate = value?.endDate || "";

  const isWeeklyInvalid =
    frequency === "WEEKLY" && selectedWeekdays.length === 0;

  const hasNoValidWeeklyOccurrences = React.useMemo(() => {
    if (
      frequency !== "WEEKLY" ||
      !startDate ||
      !endDate ||
      selectedWeekdays.length === 0
    ) {
      return false;
    }

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (start.isSame(end, "day")) {
      const dayOfWeek = start.day();
      return !selectedWeekdays.includes(dayOfWeek);
    }

    let currentDate = start;
    while (currentDate.isBefore(end, "day") || currentDate.isSame(end, "day")) {
      if (selectedWeekdays.includes(currentDate.day())) {
        return false;
      }
      currentDate = currentDate.add(1, "day");
    }

    return true;
  }, [frequency, startDate, endDate, selectedWeekdays]);

  const isMonthlyInvalid = useMemo(() => {
    if (
      frequency !== "MONTHLY" ||
      !startDate ||
      !endDate ||
      monthlyDates.length === 0
    ) {
      return false;
    }

    const filledDates = monthlyDates.filter((d) => !!d);
    if (filledDates.length === 0) return false;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    for (const date of filledDates) {
      const dayNumber = new Date(date).getDate();

      let currentMonth = start.startOf("month");
      const endMonth = end.startOf("month");
      let hasAtLeastOneValidMonth = false;

      while (
        currentMonth.isBefore(endMonth, "month") ||
        currentMonth.isSame(endMonth, "month")
      ) {
        const daysInMonth = currentMonth.daysInMonth();
        if (dayNumber <= daysInMonth) {
          const candidateDate = currentMonth.date(dayNumber);
          if (
            candidateDate.isValid() &&
            candidateDate.date() === dayNumber &&
            (candidateDate.isAfter(start, "day") ||
              candidateDate.isSame(start, "day")) &&
            (candidateDate.isBefore(end, "day") ||
              candidateDate.isSame(end, "day"))
          ) {
            hasAtLeastOneValidMonth = true;
            break;
          }
        }
        currentMonth = currentMonth.add(1, "month");
      }

      if (!hasAtLeastOneValidMonth) {
        return true;
      }
    }

    return false;
  }, [frequency, startDate, endDate, monthlyDates]);
  const isCustomInvalid = frequency === "CUSTOM" && customDates.length === 0;
  const isIntervalInvalid = false;

  const hasDuplicateDayNumbers = useMemo(() => {
    if (frequency !== "MONTHLY") return false;
    const filledDates = monthlyDates.filter((d) => !!d);
    if (filledDates.length < 2) return false;
    const dayNumbers = filledDates.map((d) => new Date(d).getDate());
    const uniqueDayNumbers = new Set(dayNumbers);
    return uniqueDayNumbers.size < dayNumbers.length;
  }, [frequency, monthlyDates]);

  const isEndDateBeforeStartDate = useMemo(() => {
    if (!startDate || !endDate) return false;
    return (
      dayjs(endDate).isBefore(dayjs(startDate), "day") ||
      dayjs(endDate).isSame(dayjs(startDate), "day")
    );
  }, [startDate, endDate]);

  const isValid =
    !!startDate &&
    !!endDate &&
    frequency &&
    !isEndDateBeforeStartDate &&
    !hasDuplicateDayNumbers &&
    !hasNoValidWeeklyOccurrences &&
    !isMonthlyInvalid &&
    ((frequency === "WEEKLY" && selectedWeekdays.length > 0) ||
      (frequency === "MONTHLY" && monthlyDates.some((d) => !!d)) ||
      (frequency === "CUSTOM" && customDates.length > 0));

  const emitChange = (updates: any = {}) => {
    const newValue = {
      startDate,
      endDate,
      frequency,
      interval,
      selectedWeekdays,
      monthlyDates,
      customDates,
      ...updates,
    };
    onChange?.(newValue);
  };

  useEffect(() => {
    if (
      disableStartDate &&
      taskStartDate &&
      value?.startDate !== taskStartDate
    ) {
      emitChange({ startDate: taskStartDate });
    }
  }, [taskStartDate, disableStartDate]);

  useEffect(() => {
    if (
      frequency === "MONTHLY" &&
      value?.monthlyDates &&
      Array.isArray(value.monthlyDates) &&
      value.monthlyDates.length > 0
    ) {
      const targetInterval =
        value.interval !== undefined && value.interval > 0
          ? value.interval
          : value.monthlyDates.length;

      setInterval((prevInterval) => {
        if (prevInterval !== targetInterval) {
          return targetInterval;
        }
        return prevInterval;
      });

      setMonthlyDates((prevMonthlyDates) => {
        const arraysEqual =
          prevMonthlyDates.length === value.monthlyDates.length &&
          prevMonthlyDates.every(
            (date, index) => date === value.monthlyDates[index],
          );

        if (!arraysEqual) {
          return [...value.monthlyDates];
        }
        return prevMonthlyDates;
      });
    }
  }, [value?.monthlyDates, value?.interval, value?.frequency, frequency]);

  useEffect(() => {
    if (frequency === "MONTHLY") {
      setSelectedWeekdays([]);
      setCustomDates([]);

      if (startDate) {
        const dayOfMonth = dayjs(startDate).date();
        const monthlyDateString = dayjs(startDate).format("YYYY-MM-DD");
        setMonthlyDates([monthlyDateString]);
        setInterval(1);
        emitChange({
          monthlyDates: [monthlyDateString],
          interval: 1,
          selectedWeekdays: [],
          customDates: [],
        });
      }
    }
    if (frequency === "WEEKLY") {
      setCustomDates([]);
    }
    if (frequency === "CUSTOM") {
      setSelectedWeekdays([]);
      setMonthlyDates([]);
      setInterval(1);
    }
    if (frequency !== "MONTHLY") {
      const emptyDates: string[] = [];
      setMonthlyDates(emptyDates);
      setInterval(1);
      if (frequency && frequency !== "CUSTOM") {
        emitChange({
          monthlyDates: emptyDates,
          interval: 1,
          selectedWeekdays: [],
          customDates: [],
        });
      }
      return;
    }

    setMonthlyDates((prev) => {
      if (interval > prev.length) {
        return [...prev, ...Array(interval - prev.length).fill("")];
      }
      if (interval < prev.length) {
        return prev.slice(0, interval);
      }
      return prev;
    });
  }, [frequency, interval, startDate]);

  useEffect(() => {
    if (!frequency || frequency !== "MONTHLY") return;
    if (!startDate) return;

    const dayOfMonth = dayjs(startDate).date();
    const monthlyDateString = dayjs(startDate).format("YYYY-MM-DD");

    setMonthlyDates([monthlyDateString]);
    emitChange({ monthlyDates: [monthlyDateString] });
  }, [startDate, frequency]);

  useEffect(() => {
    if (!frequency || frequency !== "MONTHLY") return;
    if (!hasDuplicateDayNumbers) return;

    const dayNumbersMap = new Map<number, number>();
    const uniqueDates: string[] = [];
    const filledDates = monthlyDates.filter((d) => !!d);

    filledDates.forEach((date) => {
      const dayNumber = new Date(date).getDate();
      if (!dayNumbersMap.has(dayNumber)) {
        dayNumbersMap.set(dayNumber, uniqueDates.length);
        uniqueDates.push(date);
      }
    });

    const filledDatesCount = filledDates.length;
    if (filledDatesCount > 0 && uniqueDates.length < filledDatesCount) {
      const newInterval = Math.max(uniqueDates.length, 1);
      const updatedMonthlyDates = [...uniqueDates];

      if (newInterval !== interval || uniqueDates.length !== filledDatesCount) {
        while (updatedMonthlyDates.length < newInterval) {
          updatedMonthlyDates.push("");
        }

        if (newInterval !== interval) {
          setInterval(newInterval);
        }
        if (
          JSON.stringify(updatedMonthlyDates) !== JSON.stringify(monthlyDates)
        ) {
          setMonthlyDates(updatedMonthlyDates);
          emitChange({
            interval: newInterval,
            monthlyDates: updatedMonthlyDates,
          });
        }
      }
    }
  }, [hasDuplicateDayNumbers, frequency, monthlyDates, interval]);

  useEffect(() => {
    onValidityChange?.(isValid);
    onDuplicateDayNumbersChange?.(hasDuplicateDayNumbers);
  }, [
    isValid,
    hasDuplicateDayNumbers,
    onValidityChange,
    onDuplicateDayNumbersChange,
  ]);

  const handleMonthlyDateChange = (index: number, dayNumber: number | null) => {
    if (!dayNumber || !startDate || !endDate) {
      setMonthlyDates((prev) => {
        const updated = [...prev];
        updated[index] = "";
        emitChange({ monthlyDates: updated });
        return updated;
      });
      return;
    }

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    let validDate: dayjs.Dayjs | null = null;

    let currentMonth = start.startOf("month");
    const endMonth = end.startOf("month");

    while (
      currentMonth.isBefore(endMonth, "month") ||
      currentMonth.isSame(endMonth, "month")
    ) {
      const daysInMonth = currentMonth.daysInMonth();

      if (dayNumber <= daysInMonth) {
        const candidateDate: dayjs.Dayjs = currentMonth.date(dayNumber);

        if (
          candidateDate.isValid() &&
          candidateDate.date() === dayNumber &&
          (candidateDate.isAfter(start, "day") ||
            candidateDate.isSame(start, "day")) &&
          (candidateDate.isBefore(end, "day") ||
            candidateDate.isSame(end, "day"))
        ) {
          validDate = candidateDate;
          break;
        }
      }

      currentMonth = currentMonth.add(1, "month");
    }

    if (validDate) {
      const dateString = validDate.format("YYYY-MM-DD");
      setMonthlyDates((prev) => {
        const updated = [...prev];
        updated[index] = dateString;
        emitChange({ monthlyDates: updated });
        return updated;
      });
    }
  };

  const getOrdinalDay = (dateString: string) => {
    if (!dateString) return "";
    const day = new Date(dateString).getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";
    return `${day}${suffix}`;
  };

  const datesSelected = Boolean(startDate && endDate);

  const handleCustomDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;

    const dateString = date.format("YYYY-MM-DD");

    if (customDates.includes(dateString)) {
      return;
    } else {
      const updatedDates = [...customDates, dateString].sort();
      setCustomDates(updatedDates);
      setLastSelectedCustomDate(dateString);
      emitChange({ customDates: updatedDates });
    }
  };

  const removeCustomDate = (dateToRemove: string) => {
    const updatedDates = customDates.filter((d) => d !== dateToRemove);
    setCustomDates(updatedDates);
    if (dateToRemove === lastSelectedCustomDate) {
      setLastSelectedCustomDate(
        updatedDates.length > 0 ? updatedDates[updatedDates.length - 1] : "",
      );
    }
    emitChange({ customDates: updatedDates });
  };

  useEffect(() => {
    if (!startDate || !endDate) return;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (end.isBefore(start, "day") || end.isSame(start, "day")) {
      emitChange({ endDate: "" });
    }
  }, [startDate]);

  useEffect(() => {
    if (frequency !== "CUSTOM" || !startDate || !endDate) return;
    const datesArray = Array.isArray(customDates) ? customDates : [];
    if (datesArray.length === 0) return;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    const validDates = datesArray.filter((date) => {
      const dateObj = dayjs(date);
      return (
        (dateObj.isAfter(start, "day") || dateObj.isSame(start, "day")) &&
        (dateObj.isBefore(end, "day") || dateObj.isSame(end, "day"))
      );
    });

    if (validDates.length !== datesArray.length) {
      setCustomDates(validDates);
      setLastSelectedCustomDate("");
      emitChange({ customDates: validDates });
    }
  }, [endDate, frequency]);

  useEffect(() => {
    if (frequency !== "MONTHLY" || !startDate || !endDate) return;

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const daysDifference = end.diff(start, "day") + 1;

    if (daysDifference < 31 && interval > daysDifference) {
      const newInterval = Math.max(daysDifference, 1);
      setInterval(newInterval);

      setMonthlyDates((prev) => {
        if (newInterval < prev.length) {
          const trimmed = prev.slice(0, newInterval);
          emitChange({ interval: newInterval, monthlyDates: trimmed });
          return trimmed;
        }
        return prev;
      });
    }
  }, [startDate, endDate, frequency, interval]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography>
          Start Date
          <span style={{ color: "red" }}>*</span>
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            open={startOpen}
            onOpen={() => {
              setStartOpen(true);
              setEndOpen(false);
              setCustomDateOpen(false);
              onRecurrenceDatePickerOpen?.();
            }}
            onClose={() => setStartOpen(false)}
            value={startDate ? dayjs(startDate) : null}
            onChange={(date) => {
              if (!disableStartDate) {
                const dateString = date ? date.format("YYYY-MM-DD") : "";
                emitChange({ startDate: dateString });
              }
            }}
            format="DD-MM-YYYY"
            disablePast
            disabled={disableStartDate}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                onClick: (e: any) => {
                  if (!disableStartDate) {
                    e.stopPropagation();
                    setEndOpen(false);
                    setCustomDateOpen(false);
                    setStartOpen(true);
                    onRecurrenceDatePickerOpen?.();
                  }
                },
                onBlur: (e: any) => {
                  if (disableStartDate) return;
                  const inputValue = e.target.value;
                  if (!inputValue) return;

                  let parsedDate = dayjs(inputValue, "DD-MM-YYYY", true);

                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue, "DD/MM/YYYY", true);
                  }
                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue, "YYYY-MM-DD", true);
                  }
                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue);
                  }

                  if (parsedDate.isValid()) {
                    const today = dayjs().startOf("day");
                    if (parsedDate.isBefore(today, "day")) {
                      const currentDate = today.format("YYYY-MM-DD");
                      emitChange({ startDate: currentDate });
                    } else {
                      const dateString = parsedDate.format("YYYY-MM-DD");
                      emitChange({ startDate: dateString });
                    }
                  }
                },
                sx: {
                  cursor: disableStartDate ? "not-allowed" : "text",
                  "& .MuiOutlinedInput-root": {
                    cursor: disableStartDate ? "not-allowed" : "text",
                    "& input": {
                      cursor: disableStartDate ? "not-allowed" : "text",
                    },
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography>
          End Date
          <span style={{ color: "red" }}>*</span>
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            open={endOpen}
            onOpen={() => {
              setEndOpen(true);
              setStartOpen(false);
              setCustomDateOpen(false);
              onRecurrenceDatePickerOpen?.();
            }}
            onClose={() => setEndOpen(false)}
            value={endDate ? dayjs(endDate) : null}
            onChange={(date) => {
              const dateString = date ? date.format("YYYY-MM-DD") : "";
              emitChange({ endDate: dateString });
            }}
            format="DD-MM-YYYY"
            minDate={startDate ? dayjs(startDate).add(1, "day") : undefined}
            slotProps={{
              day: {
                sx: {
                  "&.MuiPickersDay-today": {
                    border: "none",
                    backgroundColor: "transparent",
                  },
                  "&.MuiPickersDay-today:not(.Mui-selected)": {
                    border: "none",
                    backgroundColor: "transparent",
                  },
                },
              },
              textField: {
                required: true,
                fullWidth: true,
                size: "small",
                onClick: (e: any) => {
                  e.stopPropagation();
                  setStartOpen(false);
                  setCustomDateOpen(false);
                  setEndOpen(true);
                  onRecurrenceDatePickerOpen?.();
                },
                onBlur: (e: any) => {
                  const inputValue = e.target.value;
                  if (!inputValue) return;

                  let parsedDate = dayjs(inputValue, "DD-MM-YYYY", true);

                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue, "DD/MM/YYYY", true);
                  }
                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue, "YYYY-MM-DD", true);
                  }
                  if (!parsedDate.isValid()) {
                    parsedDate = dayjs(inputValue);
                  }

                  if (parsedDate.isValid()) {
                    const today = dayjs().startOf("day");
                    const minDate = startDate
                      ? dayjs(startDate).add(1, "day")
                      : today;

                    if (parsedDate.isBefore(today, "day")) {
                      const currentDate = today.format("YYYY-MM-DD");
                      emitChange({ endDate: currentDate });
                    } else if (
                      startDate &&
                      (parsedDate.isBefore(minDate, "day") ||
                        parsedDate.isSame(dayjs(startDate), "day"))
                    ) {
                      emitChange({ endDate: minDate.format("YYYY-MM-DD") });
                    } else {
                      const dateString = parsedDate.format("YYYY-MM-DD");
                      emitChange({ endDate: dateString });
                    }
                  }
                },
                sx: {
                  cursor: "text",
                  "& .MuiOutlinedInput-root": {
                    cursor: "text",
                    "& input": {
                      cursor: "text",
                    },
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <RepeatIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
            Repeat every
          </Typography>
          <TextField
            select
            size="small"
            value={frequency || ""}
            disabled={!datesSelected}
            onChange={(e) => {
              const newPattern = e.target.value as
                | "WEEKLY"
                | "MONTHLY"
                | "CUSTOM";
              setFrequency(newPattern);
              if (newPattern === "MONTHLY") {
                setSelectedWeekdays([]);
                setCustomDates([]);
                setMonthlyDates(Array(interval).fill(""));
              } else if (newPattern === "WEEKLY") {
                setMonthlyDates([]);
                setCustomDates([]);
              } else if (newPattern === "CUSTOM") {
                setSelectedWeekdays([]);
                setMonthlyDates([]);
                setCustomDates([]);
                setLastSelectedCustomDate("");
              }
              emitChange({
                frequency: newPattern,
                selectedWeekdays: newPattern === "WEEKLY" ? [] : [],
                monthlyDates:
                  newPattern === "MONTHLY" ? Array(interval).fill("") : [],
                customDates: newPattern === "CUSTOM" ? [] : [],
              });
            }}
            sx={{
              minWidth: 110,
              maxWidth: 110,
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
                height: "36px",
                "& fieldset": {
                  borderColor: "#E0E0E0",
                },
                "&:hover fieldset": {
                  borderColor: "#E0E0E0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#E0E0E0",
                },
              },
              "& .MuiSelect-select": {
                paddingTop: "8px",
                paddingBottom: "8px",
              },
            }}
          >
            <MenuItem value="WEEKLY">Week</MenuItem>
            <MenuItem value="MONTHLY">Month</MenuItem>
            <MenuItem value="CUSTOM">Custom</MenuItem>
          </TextField>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        {frequency === "WEEKLY" ? (
          <>
            <Typography>
              Occurs On
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {WEEKDAYS.map((day) => {
                const isSelected = selectedWeekdays.includes(day.value);

                return (
                  <Chip
                    key={day.value}
                    label={day.label.charAt(0)}
                    clickable
                    onClick={() => {
                      setSelectedWeekdays((prev) => {
                        const updated = isSelected
                          ? prev.filter((d) => d !== day.value)
                          : [...prev, day.value];
                        emitChange({ selectedWeekdays: updated });
                        return updated;
                      });
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      fontWeight: 600,
                      fontSize: 14,
                      bgcolor: isSelected ? "primary.main" : "grey.200",
                      color: isSelected
                        ? "primary.contrastText"
                        : "text.primary",
                      "&:hover": {
                        bgcolor: isSelected ? "primary.dark" : "grey.300",
                      },
                    }}
                  />
                );
              })}
            </Box>
            {isWeeklyInvalid && (
              <Typography variant="caption" color="error">
                Please select at least one weekday
              </Typography>
            )}
            {hasNoValidWeeklyOccurrences && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                No occurrences. Try adjusting the End date or weekdays.
              </Typography>
            )}
            {selectedWeekdays.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                {[...selectedWeekdays]
                  .sort((a, b) => a - b)
                  .map((v) => WEEKDAYS.find((d) => d.value === v)?.label)
                  .join(", ")}
              </Typography>
            )}
          </>
        ) : frequency === "CUSTOM" ? (
          <>
            <Typography>
              Select Custom Dates (Multiple Allowed)
              <span style={{ color: "red" }}>*</span>
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                open={customDateOpen}
                onOpen={() => {
                  setCustomDateOpen(true);
                  setStartOpen(false);
                  setEndOpen(false);
                  onRecurrenceDatePickerOpen?.();
                }}
                onClose={() => setCustomDateOpen(false)}
                value={
                  lastSelectedCustomDate ? dayjs(lastSelectedCustomDate) : null
                }
                onChange={handleCustomDateChange}
                format="DD-MM-YYYY"
                minDate={startDate ? dayjs(startDate) : undefined}
                maxDate={endDate ? dayjs(endDate) : undefined}
                shouldDisableDate={(date) => {
                  if (!date) return false;
                  const dayjsDate = dayjs(date);
                  if (!dayjsDate.isValid()) return false;
                  const dateString = dayjsDate.format("YYYY-MM-DD");
                  return customDates.includes(dateString);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder: "Click to select multiple dates",
                    onClick: (e: any) => {
                      e.stopPropagation();
                      setStartOpen(false);
                      setEndOpen(false);
                      setCustomDateOpen(true);
                      onRecurrenceDatePickerOpen?.();
                    },
                    inputProps: {
                      readOnly: true,
                    },
                    sx: {
                      cursor: "pointer",
                      "& .MuiOutlinedInput-root": {
                        cursor: "pointer",
                        "& fieldset": {
                          borderColor: "rgba(0, 0, 0, 0.23) !important",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(0, 0, 0, 0.23) !important",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2 !important",
                          borderWidth: "1px !important",
                        },
                        "&.Mui-error fieldset": {
                          borderColor: "rgba(0, 0, 0, 0.23) !important",
                        },
                        "& input": {
                          cursor: "pointer",
                          caretColor: "transparent",
                        },
                      },
                    },
                  },
                  day: {
                    sx: {
                      "&.Mui-disabled": {
                        backgroundColor: "transparent !important",
                        color: "rgba(0, 0, 0, 0.38) !important",
                      },
                      "&.MuiPickersDay-hiddenDaySpacingFiller": {
                        backgroundColor: "transparent !important",
                      },
                      "&:not(.MuiPickersDay-dayOutsideMonth)": {
                        "&.Mui-disabled": {
                          backgroundColor: "transparent !important",
                        },
                      },
                    },
                  },
                  desktopPaper: {
                    sx: {
                      "& .MuiPickersDay-root": {
                        "&.Mui-disabled": {
                          backgroundColor: "transparent !important",
                          color: "rgba(0, 0, 0, 0.38) !important",
                        },
                        "&.MuiPickersDay-hiddenDaySpacingFiller": {
                          backgroundColor: "transparent !important",
                        },
                      },
                      "& .MuiDayCalendar-weekContainer": {
                        "& .MuiPickersDay-root.Mui-disabled": {
                          backgroundColor: "transparent !important",
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            {isCustomInvalid && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                Please select at least one custom date
              </Typography>
            )}
          </>
        ) : (
          <></>
        )}
      </Grid>

      {frequency === "CUSTOM" && customDates.length > 0 && (
        <Grid item xs={12}>
          <Typography
            variant="body2"
            sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}
          >
            Selected Dates ({customDates.length}):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {customDates.map((date, index) => (
              <Chip
                key={index}
                label={dayjs(date).format("DD MMM YYYY")}
                onDelete={() => removeCustomDate(date)}
                sx={{
                  minWidth: "130px",
                  width: "130px",
                  bgcolor: "#11c89b",
                  color: "#ffffff",
                  border: "1px solid #11c89b",
                  justifyContent: "space-between",
                  "& .MuiChip-label": {
                    flex: 1,
                    textAlign: "left",
                    paddingLeft: "12px",
                    paddingRight: "4px",
                  },
                  "& .MuiChip-deleteIcon": {
                    color: "#ffffff",
                    margin: 0,
                    marginRight: "8px",
                    "&:hover": {
                      color: "#f0f0f0",
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Grid>
      )}

      {frequency === "MONTHLY" && isMonthlyInvalid && (
        <Grid item xs={12}>
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 0.5 }}
          >
            The selected day number does not exist in any month within the date
            range
          </Typography>
        </Grid>
      )}

      {frequency && isValid && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              bgcolor: "#E6F7F1",
              borderRadius: 1,
            }}
          >
            <EventRepeatIcon sx={{ fontSize: 14, color: "#11C89B" }} />
            <Typography variant="body2" sx={{ color: "#546E7A" }}>
              {(() => {
                if (frequency === "WEEKLY" && selectedWeekdays.length > 0) {
                  const dayNames = [...selectedWeekdays]
                    .sort((a, b) => a - b)
                    .map((v) =>
                      WEEKDAYS.find((d) => d.value === v)?.label.substring(
                        0,
                        3,
                      ),
                    )
                    .join(", ");
                  return `Occurs every ${dayNames} until ${endDate ? dayjs(endDate).format("DD MMM YYYY") : "end date"}`;
                }

                if (frequency === "MONTHLY" && startDate) {
                  const dayOfMonth = dayjs(startDate).date();
                  const ordinal = getOrdinalDay(startDate);
                  return `Occurs on ${ordinal} of every month until ${endDate ? dayjs(endDate).format("DD MMM YYYY") : "end date"}`;
                }

                if (frequency === "CUSTOM" && customDates.length > 0) {
                  const dateCount = customDates.length;
                  return `Scheduled on ${dateCount} custom date${dateCount > 1 ? "s" : ""}`;
                }

                return "";
              })()}
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
}
